import { Preview, Refresh } from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"
import moment from "moment"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { JsonView } from "react-json-view-lite"
import { useLocation } from "react-router-dom"
import { ReservationAud } from "shared/reservationAud"
import { ReservationAudThingTypeMrn } from "shared/reservationAudData"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { selectSaList, thingsActions } from "src/features/things/thingsSlice"
import { useSearchParamsState } from "src/hooks/useSearchParamsState"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import { selectMeIsReady } from "../features/me/meSlice"
import AuditTrailEventFilter from "./AuditTrailEventFilter"
import SaLayout from "./SaLayout"
import Sl2DataGrid from "./Sl2DataGrid"

const SubpageTabMap: Record<string, number> = {
  byEvent: 0,
  byEmployeeCode: 1,
}

const pageSize = 100

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

const tonalIconBtnSx = {
  bgcolor: "rgba(35, 66, 133, 0.06)",
  color: "primary.main",
  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
}

export const SaReservationAudPage: FC = () => {
  const { t } = useTranslation()
  const isReady = useAppSelector(selectMeIsReady)

  const location = useLocation()

  const dispatch = useAppDispatch()

  const [searchParamsString, setSearchParamsString] = useSearchParamsState(
    "searchParams",
    "{}",
  )

  const [nextToken0, setNextToken0] = useState<string | undefined | null>()

  const [nextToken1, setNextToken1] = useState<string | undefined | null>()

  const searchParams = useMemo(() => {
    try {
      return JSON.parse(searchParamsString)
    } catch (e) {
      console.error("Error parsing searchParams", e)
      return {}
    }
  }, [searchParamsString])

  const isByEvent = searchParams.type === "byEvent"

  const qByEvent = useGetThingsQuery(
    {
      params: {
        "ap.name": isByEvent
          ? SharedAccessPatternSaEnum.SaGetReservationAudsByEvent
          : SharedAccessPatternSaEnum.SaGetReservationAudsByEmployeeCode,
        "ap.event": isByEvent ? searchParams?.event : undefined,
        "ap.employeeCode": isByEvent ? undefined : searchParams?.employeeCode,
        "ap.limit": pageSize + 1,
        "ap.nextToken": isByEvent ? nextToken0 : nextToken1,
      },
    },
    { skip: !isReady },
  )
  const setSearchParams = useCallback(
    (searchParams: Record<string, any>) => {
      dispatch(thingsActions.deleteByThingTypeMrn(ReservationAudThingTypeMrn))

      setNextToken0(undefined)
      setNextToken1(undefined)

      setSearchParamsString(JSON.stringify(searchParams))

      dispatch(thingsActions.clearSaList())
    },
    [dispatch, setSearchParamsString],
  )

  // if no subpage in url, redirect to byEvent
  useCallback(() => {
    if (!location.search.includes("searchParams")) {
      setSearchParams({ type: "byEvent", event: "updated" })
    }
  }, [location.search, setSearchParams])

  const [localEmployeeCode, setLocalEmployeeCode] = useState<string>(
    searchParams.employeeCode || "",
  )

  const audData = useAppSelector((state) =>
    selectSaList<ReservationAud>(state),
  ).filter((t: any) => t.thingTypeMrn === ReservationAudThingTypeMrn)

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      // get subpage from map with newValue
      const subpage = Object.keys(SubpageTabMap).find(
        (key) => SubpageTabMap[key] === newValue,
      )

      dispatch(thingsActions.clearSaList())

      setSearchParams({ ...searchParams, type: subpage, event: "updated" })
    },
    [dispatch, searchParams, setSearchParams],
  )

  const [previewDetails, setPreviewDetails] = useState<Record<
    string,
    any
  > | null>(null)

  const columns = useMemo(
    () => [
      // action "preview" icon column
      {
        field: "id",
        headerName: "",
        width: 50,
        renderCell: (params: any) => {
          return (
            <Stack direction="row" spacing={1}>
              <IconButton
                size="small"
                onClick={() => setPreviewDetails(params.row || null)}
              >
                <Preview />
              </IconButton>
            </Stack>
          )
        },
      },
      {
        field: "createdAt",
        headerName: "Created At",
        width: 150,
        renderCell: (params: any) => {
          return (
            <Stack direction="column" spacing={1}>
              <span>{moment(params.value).format("YYYY-MM-DD HH:mm:ss")}</span>
              <span>{moment(params.value).fromNow()}</span>
            </Stack>
          )
        },
      },
      {
        field: "event",
        headerName: "Event",
        width: 150,

        //  render as Chip
        renderCell: (params: any) => {
          return <Chip label={params.value} />
        },
      },
      {
        field: "employeeCode",
        headerName: "Employee Code",
        width: 150,

        renderCell: (params: any) => {
          return params?.value ? (
            <Chip label={params.value} color="primary" variant="outlined" />
          ) : (
            "n/a"
          )
        },
      },
      { field: "tenantId", headerName: "Tenant ID", width: 300 },
      { field: "lockerId", headerName: "Locker ID", width: 300 },
      { field: "reservationId", headerName: "reservation ID", width: 300 },
    ],
    [],
  )

  const isRefreshing = qByEvent.isLoading || qByEvent.isFetching

  return (
    <SaLayout selectedMenuItemLabelKey="reservationAudit">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "primary.main",
            }}
          >
            {t("adminApp")}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              mt: 0.5,
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            {t("reservationAudit")}
          </Typography>
          <Typography
            sx={{
              mt: 1.25,
              color: "text.secondary",
              maxWidth: 720,
              lineHeight: 1.6,
              fontSize: "0.95rem",
            }}
          >
            {t("reservationAuditDescription")}
          </Typography>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "rgba(0, 0, 0, 0.08)",
            mb: 2.5,
          }}
        >
          <Tabs
            value={SubpageTabMap[searchParams.type || "byEvent"]}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              "& .MuiTab-root": { minHeight: 40 },
            }}
          >
            <Tab data-ui="UI-SL2W-204"
              label={
                <Typography variant="body2" fontWeight={"bold"}>
                  {t("byEvent")}
                </Typography>
              }
              sx={{
                textTransform: "none",
              }}
            />
            <Tab data-ui="UI-SL2W-205"
              label={
                <Typography variant="body2" fontWeight={"bold"}>
                  {t("byEmployeeCode")}
                </Typography>
              }
              sx={{
                textTransform: "none",
              }}
            />
          </Tabs>
        </Box>

        {searchParams.type === "byEvent" && (
          <Stack direction="column" spacing={2.5}>
            {/* Filter card */}
            <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                rowGap={1.5}
              >
                <AuditTrailEventFilter
                  value={searchParams.event}
                  onChange={(event) => {
                    setSearchParams({ ...searchParams, event })
                  }}
                />
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.875rem",
                    }}
                  >
                    {`${audData.length} ${t("events")}`}
                  </Typography>
                  {isRefreshing ? (
                    <CircularProgress size={22} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => qByEvent.refetch()}
                      sx={tonalIconBtnSx}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Box>

            {/* Data grid card */}
            <Box sx={{ ...cardSx, overflow: "hidden" }}>
              <Sl2DataGrid
                datagridProps={{
                  rows: audData,
                  columns,
                }}
                pageSize={pageSize}
                isLoading={qByEvent.isLoading}
                listCount={audData.length || 0}
                onNextTokenChange={() =>
                  setNextToken0(JSON.stringify(qByEvent.data?.meta?.nextToken))
                }
              />
            </Box>
          </Stack>
        )}
        {searchParams.type === "byEmployeeCode" && (
          <Stack direction="column" spacing={2.5}>
            {/* Filter card */}
            <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                rowGap={1.5}
              >
                <TextField
                  label="Employee Code"
                  size="small"
                  sx={{ minWidth: 260 }}
                  value={localEmployeeCode}
                  onChange={(e) => setLocalEmployeeCode(e.target.value)}
                  onBlur={(e) =>
                    setSearchParams({
                      ...searchParams,
                      employeeCode: e.target.value,
                    })
                  }
                />
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.875rem",
                    }}
                  >
                    {`${audData.length || 0} ${t("events")}`}
                  </Typography>
                  {isRefreshing ? (
                    <CircularProgress size={22} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => qByEvent.refetch()}
                      sx={tonalIconBtnSx}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Box>

            {/* Data grid card */}
            <Box sx={{ ...cardSx, overflow: "hidden" }}>
              <Sl2DataGrid
                datagridProps={{
                  rows: audData,
                  columns,
                }}
                pageSize={pageSize}
                isLoading={qByEvent.isLoading}
                listCount={audData.length || 0}
                onNextTokenChange={() =>
                  setNextToken1(JSON.stringify(qByEvent.data?.meta?.nextToken))
                }
              />
            </Box>
          </Stack>
        )}
      </Box>
      <Dialog data-ui="UI-SL2W-206"
        open={!!previewDetails}
        onClose={() => setPreviewDetails(null)}
        fullWidth
        maxWidth="md"
        sx={{
          maxHeight: "100%",
        }}
      >
        <Stack direction="column" spacing={1} p={2}>
          <Typography variant="body1">{`${t("preview")} : ${
            previewDetails?.id || "-"
          }`}</Typography>
          <JsonView data={previewDetails || {}} />
        </Stack>
      </Dialog>
    </SaLayout>
  )
}

export default SaReservationAudPage
